import { TrashIcon } from "assets/images";
import { useAppSelector } from "hooks/redux";
import React from "react";
import { WorkspaceTab } from "store/reducers/SettingsSlice";
import styled from "styled-components";

interface TabsIndicatorProps {
    workspaceTabs?: WorkspaceTab[];
    activeWorkspaceId?: number;
    onTabClick?: (workspaceId: number) => void;
    onAddTabClick?: () => void;
    onDeleteClick?: (workspaceId: number) => void;
}

const TabsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
    padding-bottom: 6px;
    max-height: 82px;
    overflow-y: scroll;
`;

const Tab = styled.div<{ $isActive: boolean }>`
    padding: 6px 12px;
    background: ${(props) =>
        props.$isActive ? "rgba(219, 234, 219, 1)" : "transparent"};
    border: 1px solid
        ${(props) => (props.$isActive ? "rgb(124, 190, 123)" : "#d9d9d9")};
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    white-space: nowrap;
    transition: all 0.3s ease;
    user-select: none;
    color: ${(props) => (props.$isActive ? "black" : "#595959")};
    font-weight: ${(props) => (props.$isActive ? "500" : "400")};

    &:hover {
        background: ${(props) =>
            props.$isActive ? "rgba(162, 218, 161, 1)" : "#e8e8e8"};
        border-color: ${(props) =>
            props.$isActive ? "rgb(98, 147, 97)" : "#bae7ff"};
    }

    &:active {
        transform: translateY(1px);
    }
`;
const DeleteIconWrapper = styled.div`
    position: absolute;
    top: 4px;
    right: 4px;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border-radius: 4px;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.2s ease;

    &:hover {
        background: #db9d9eff;
        color: #ff4d4f;
    }
`;

const TabWrapper = styled.div`
    position: relative;
    display: inline-block;

    &:hover ${DeleteIconWrapper} {
        opacity: 1;
    }
`;
const AddTab = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid rgb(124, 190, 123);
    border-radius: 4px;
    cursor: pointer;
    color: rgb(124, 190, 123);
    font-size: 18px;
    font-weight: 500;
    transition: all 0.3s ease;
    user-select: none;

    &:hover {
        border-color: rgb(98, 147, 97);
        color: rgb(98, 147, 97);
        background-color: #f6ffed;
    }

    &:active {
        transform: scale(0.95);
    }
`;

const TabsIndicator: React.FC<TabsIndicatorProps> = ({
    workspaceTabs = [],
    activeWorkspaceId,
    onTabClick,
    onAddTabClick,
    onDeleteClick,
}) => {
    const handleTabClick = (workspaceId: number) => {
        onTabClick?.(workspaceId);
    };
    const editMode = useAppSelector((state) => state.settings.editMode);
    const getActiveWorkspaceId = () => {
        if (activeWorkspaceId !== undefined) {
            return activeWorkspaceId;
        }
        const activeWorkspace = workspaceTabs.find((w) => w.is_active);
        return activeWorkspace ? activeWorkspace.id : workspaceTabs[0]?.id;
    };

    const activeId = getActiveWorkspaceId();

    return (
        <TabsContainer >
            {workspaceTabs.map((workspace) => (
                <TabWrapper key={workspace.id}>
                    <Tab
                        $isActive={workspace.id === activeId}
                        onClick={() => handleTabClick(workspace.id)}
                        title={
                            workspace.is_active
                                ? "Активное рабочее пространство"
                                : "Переключиться на это рабочее пространство"
                        }
                    >
                        {workspace.name}
                    </Tab>
                    {editMode && (
                        <DeleteIconWrapper
                            onClick={(e) => {
                                e.stopPropagation();
                                onDeleteClick?.(workspace.id);
                            }}
                        >
                            <TrashIcon
                                style={{ width: "14px", height: "14px" }}
                            />
                        </DeleteIconWrapper>
                    )}
                </TabWrapper>
            ))}
            <AddTab
                onClick={onAddTabClick}
                title="Создать новое рабочее пространство"
            >
                +
            </AddTab>
        </TabsContainer>
    );
};

export default TabsIndicator;
